import apiRequest from "../../../utils/apiRequest";
import { getLiveFlexSectionColumns } from "../../../utils/builderUtils";

// Applies validated Remix AI operations to the builder.
// Creates go through builder.create (POSTed immediately, flagged toBeCreated
// until the user saves any later edits). Updates and deletes stay client-side
// until save. All changes from one AI turn are recorded as a SINGLE history
// entry (one grouped builder.update call at the end), so a single undo/redo
// reverts or re-applies the whole AI command at once.

const TEMP_REFERENCE_KEYS = [
  "step_id",
  "section_id",
  "section_column_id",
  "element_id",
  "field_id",
  "content_toggle_item_id",
  "contactable_id",
  "product_id",
  "product_recommendation_id",
  "quiz_sub_field_id",
  "jump_field_id",
];

const VARIANT_SCOPED_TYPES = ["steps", "sections", "elements"];
const NESTED_CHILD_TYPES = ["steps", "sections", "elements", "section_columns"];
const DUPLICATABLE_TYPES = ["steps", "sections", "elements", "section_columns"];

const CONDITION_ANSWER_MAPPING_TYPES = [
  "If answer to question is",
  "If answer to question is NOT",
  "If answer to question is greater than",
  "If answer to question is less than",
];

const CONDITION_ANSWER_DATA_KEYS = ["slug", "field_id", "field_option_id"];

// Font-family option keys the builder exposes end in "-font-family" or the
// shorthand "-font" (e.g. button-font, timer-number-font); "-font-color",
// "-font-weight", "-font-size", "-font-line-height" are NOT font families.
const FONT_FAMILY_KEY = /(?:-font-family|-font)$/;

function isFontFamilyKey(key) {
  return typeof key === "string" && FONT_FAMILY_KEY.test(key);
}

// Mirror the per-setting font select callbacks (ctas.builder.fonts
// .load_font_family) so AI-applied fonts pull down their Google font file for
// the builder preview instead of falling back to a system font.
function loadFontFamilies(fontValues) {
  const loader = window.ctas?.builder?.fonts?.load_font_family;
  if (typeof loader !== "function") return;

  fontValues.forEach((value) => {
    try {
      loader(value);
    } catch (error) {
      // a single font failing to load should not abort the others
    }
  });
}

function collectFontValues(optionsObject, fontValues) {
  if (!optionsObject || typeof optionsObject !== "object") return;

  Object.entries(optionsObject).forEach(([key, value]) => {
    if (isFontFamilyKey(key) && typeof value === "string" && value.trim()) {
      fontValues.add(value.trim());
    }
  });
}

function resolveValue(value, idMap) {
  if (typeof value === "string" && idMap[value] !== undefined) {
    return idMap[value];
  }
  return value;
}

// Per-answer skips are field_option.jump_field_id. Models often send the
// field/action_group key (jump-field-id) instead, which the Logic tab ignores.
// Answer images belong on field_option.image (plus visual_type "image"), not options.image.
function normalizeFieldOptionAttributes(attributes) {
  if (!attributes || typeof attributes !== "object") return attributes;

  const next = { ...attributes };
  const options =
    next.options &&
    typeof next.options === "object" &&
    !Array.isArray(next.options)
      ? { ...next.options }
      : null;
  const jump =
    next.jump_field_id ||
    next["jump-field-id"] ||
    (options && (options["jump-field-id"] || options.jump_field_id));
  const image = next.image || (options && options.image);

  delete next["jump-field-id"];
  if (options) {
    delete options["jump-field-id"];
    delete options.jump_field_id;
    delete options.image;
    if (Object.keys(options).length > 0) {
      next.options = options;
    } else {
      delete next.options;
    }
  }
  if (jump !== undefined && jump !== null && jump !== "") {
    next.jump_field_id = jump;
  }
  if (image !== undefined && image !== null && image !== "") {
    next.image = image;
    if (!next.visual_type) next.visual_type = "image";
  }
  return next;
}

function findRecord(collection, id) {
  if (!collection || id === undefined || id === null) return null;
  return collection[id] || collection[String(id)] || null;
}

function findField(variant, fieldId) {
  const direct = findRecord(variant.fields, fieldId);
  if (direct) return direct;

  return Object.values(variant.elements || {})
    .map((element) => findRecord(element && element.fields, fieldId))
    .find(Boolean);
}

// Quiz/form canvas reads element.fields and field.field_options (nested), not
// only the flat variant collections builder.create writes to.
function nestFieldOnElement(variant, field) {
  if (!field || field.element_id === undefined || field.element_id === null) {
    return;
  }

  const element = findRecord(variant.elements, field.element_id);
  if (!element) return;

  if (!element.fields) element.fields = {};
  element.fields[field.id] = field;
  if (!field.field_options) field.field_options = {};
}

function nestFieldOptionOnField(variant, fieldOption) {
  if (
    !fieldOption ||
    fieldOption.field_id === undefined ||
    fieldOption.field_id === null
  ) {
    return;
  }

  const field = findField(variant, fieldOption.field_id);
  if (!field) return;

  if (!field.field_options) field.field_options = {};
  field.field_options[fieldOption.id] = fieldOption;
}

// "reviews-ids" element options hold comma-separated review ids which may
// reference temp ids of reviews created earlier in the batch
function resolveIdList(value, idMap) {
  if (typeof value !== "string") return value;

  return value
    .split(",")
    .map((token) => resolveValue(token.trim(), idMap))
    .join(",");
}

function resolveAttributes(attributes, idMap) {
  const resolved = { ...(attributes || {}) };

  TEMP_REFERENCE_KEYS.forEach((key) => {
    if (resolved[key] !== undefined && resolved[key] !== null) {
      resolved[key] = resolveValue(resolved[key], idMap);
    }
  });

  ["data", "options"].forEach((nestedKey) => {
    const nested = resolved[nestedKey];
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      const resolvedNested = { ...nested };
      ["step", "field"].forEach((key) => {
        if (resolvedNested[key] !== undefined && resolvedNested[key] !== null) {
          resolvedNested[key] = resolveValue(resolvedNested[key], idMap);
        }
      });
      if (resolvedNested["reviews-ids"] !== undefined) {
        resolvedNested["reviews-ids"] = resolveIdList(
          resolvedNested["reviews-ids"],
          idMap
        );
      }
      [
        "redirect-step",
        "products-button-redirect-step",
        "video-auto-advance-step",
        "jump-field-id",
      ].forEach((key) => {
        if (resolvedNested[key] !== undefined && resolvedNested[key] !== null) {
          resolvedNested[key] = resolveValue(resolvedNested[key], idMap);
        }
      });
      resolved[nestedKey] = resolvedNested;
    }
  });

  return resolved;
}

// Flatten nested options/data hashes to leaf setting paths so a partial
// update like options.structure.padding-top does not replace the whole
// structure object (which would wipe background-color/image, etc.).
function appendSettingChanges(
  changes,
  object_type,
  object_id,
  pathPrefix,
  value
) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    Object.entries(value).forEach(([nestedKey, nestedValue]) => {
      appendSettingChanges(
        changes,
        object_type,
        object_id,
        `${pathPrefix}[${nestedKey}]`,
        nestedValue
      );
    });
    return;
  }

  changes.push({
    object_type: object_type,
    object_id: object_id,
    setting_name: pathPrefix,
    value: value,
  });
}

function buildUpdateChanges(operation, idMap) {
  const object_type = operation.object_type;
  const object_id =
    object_type === "variants"
      ? undefined
      : resolveValue(operation.object_id, idMap);
  let rawAttributes = operation.attributes;
  if (object_type === "field_options") {
    rawAttributes = normalizeFieldOptionAttributes(rawAttributes);
  }
  const attributes = resolveAttributes(rawAttributes, idMap);
  const changes = [];

  Object.entries(attributes).forEach(([key, value]) => {
    if (
      ["options", "data"].includes(key) &&
      value &&
      typeof value === "object" &&
      !Array.isArray(value)
    ) {
      appendSettingChanges(changes, object_type, object_id, `[${key}]`, value);
    } else {
      changes.push({
        object_type: object_type,
        object_id: object_id,
        setting_name: `[${key}]`,
        value: value,
      });
    }
  });

  return changes;
}

// the step following the element's own step, by position
function findNextStepId(variant, element, section_id) {
  const section = variant.sections[section_id] || {};
  const currentStep = variant.steps[element.step_id || section.step_id];
  if (!currentStep) return null;

  const nextStep = Object.values(variant.steps)
    .filter(
      (step) =>
        step.toBeDeleted !== true && step.position > currentStep.position
    )
    .sort((a, b) => a.position - b.position)[0];

  return nextStep ? nextStep.id : null;
}

function laterSectionReferencesRow(operations, index, temp_id) {
  if (!temp_id) return false;

  return operations
    .slice(index + 1)
    .some(
      (operation) =>
        operation.op === "create" &&
        operation.object_type === "sections" &&
        operation.attributes &&
        operation.attributes.element_id === temp_id
    );
}

// true when a later create builds the nested section for this toggle temp_id
function laterSectionReferencesToggle(operations, index, temp_id) {
  if (!temp_id) return false;

  return operations
    .slice(index + 1)
    .some(
      (operation) =>
        operation.op === "create" &&
        operation.object_type === "sections" &&
        operation.attributes &&
        operation.attributes.content_toggle_item_id === temp_id
    );
}

function batchCreatesListItemsFor(
  operations,
  index,
  temp_id,
  created_id,
  idMap
) {
  return operations.slice(index + 1).some((operation) => {
    if (
      operation.op !== "create" ||
      operation.object_type !== "content_list_items"
    ) {
      return false;
    }
    const element_id = operation.attributes && operation.attributes.element_id;
    if (element_id === undefined || element_id === null) return false;
    if (temp_id && element_id === temp_id) return true;
    return String(resolveValue(element_id, idMap)) === String(created_id);
  });
}

function isDefaultListPlaceholder(content) {
  const text = String(content || "")
    .replace(/<[^>]*>/g, "")
    .trim();
  return /^Item \d+$/i.test(text);
}

function removeDefaultListPlaceholders(listElement, variant, groupedUpdates) {
  const byId = {};

  Object.values(listElement.content_list_items || {}).forEach((item) => {
    if (item && item.id !== undefined) byId[item.id] = item;
  });
  Object.values(variant.content_list_items || {}).forEach((item) => {
    if (
      item &&
      item.toBeDeleted !== true &&
      String(item.element_id) === String(listElement.id)
    ) {
      byId[item.id] = item;
    }
  });

  Object.values(byId).forEach((item) => {
    if (!isDefaultListPlaceholder(item.content)) return;

    groupedUpdates.push({
      object_type: "content_list_items",
      object_id: item.id,
      setting_name: "[toBeDeleted]",
      value: true,
    });
    if (variant.content_list_items && variant.content_list_items[item.id]) {
      variant.content_list_items[item.id].toBeDeleted = true;
    }
  });
}

function sectionColumnsFor(section_id, variant, createdColumnsBySection) {
  const fromTurn = createdColumnsBySection[String(section_id)] || [];
  if (fromTurn.length > 0) return fromTurn;

  return getLiveFlexSectionColumns({ id: section_id }, variant);
}

// Real section_column ids are numeric; an unresolved temp-id (e.g. "col_right"
// whose create op hasn't run yet) is a non-numeric string that must NOT be
// POSTed verbatim.
function isResolvedColumnId(value) {
  return (
    value !== undefined &&
    value !== null &&
    value !== "" &&
    !Number.isNaN(Number(value))
  );
}

function assignSectionColumnId(payload, variant, createdColumnsBySection) {
  if (!payload) return payload;
  if (isResolvedColumnId(payload.section_column_id)) return payload;

  // drop an unresolved temp-id so it isn't sent as-is, then place by position
  delete payload.section_column_id;

  const section_id = payload.section_id;
  if (section_id === undefined || section_id === null) return payload;

  const columns = sectionColumnsFor(
    section_id,
    variant,
    createdColumnsBySection
  );
  if (columns.length === 0) return payload;

  const columnPos = parseInt(payload.column, 10);
  const hasColumnPos = !Number.isNaN(columnPos) && columnPos > 0;
  // A requested column with no matching position is left unassigned so the
  // server places it (respecting the `column` attribute) rather than silently
  // collapsing every mismatched element into the first column.
  const match = hasColumnPos
    ? columns.find((column) => Number(column.position) === columnPos)
    : columns[0];

  if (match) {
    payload.section_column_id = match.id;
  }
  return payload;
}

function findExistingSectionColumn(section_id, position, variant) {
  const columnPos = parseInt(position, 10);
  if (Number.isNaN(columnPos) || columnPos < 1) return null;

  return (
    getLiveFlexSectionColumns({ id: section_id }, variant).find(
      (column) => Number(column.position) === columnPos
    ) || null
  );
}

async function ensureSectionIsFlex(section_id, variant, builder) {
  const section =
    variant.sections &&
    (variant.sections[section_id] || variant.sections[String(section_id)]);
  if (!section) return;

  if (section.layout_type === "flex") return;

  const result = await apiRequest(
    "POST",
    `./sections/${section_id}/upgrade_to_flex`
  );
  if (!result || result.code < 200 || result.code >= 300 || !result.json) {
    throw new Error(
      (result && result.json && result.json.error) ||
        "Failed to upgrade section to flex columns"
    );
  }

  const upgraded = result.json.section || {};
  section.layout_type = upgraded.layout_type || "flex";
  if (upgraded.options) section.options = upgraded.options;

  if (!variant.section_columns) variant.section_columns = {};
  (result.json.section_columns || []).forEach((column) => {
    if (column && column.id !== undefined) {
      variant.section_columns[column.id] = column;
    }
  });

  builder.update([
    {
      object_type: "sections",
      object_id: section.id,
      setting_name: "[layout_type]",
      value: "flex",
      skip_history: true,
    },
  ]);
}

// creates skip per-object history; the caller records them in the turn's
// grouped history entry instead
async function createObject(builder, object_type, payload, groupedUpdates) {
  const createPayload =
    object_type === "sections" ? { ...payload, layout_type: "flex" } : payload;

  const created = await builder.create(object_type, createPayload, {
    skip_select: true,
    skip_history: true,
    callback: (object) => {
      // register nested children (e.g. quiz fields, row sections) returned by
      // the create endpoints via to_prop({ children: true })
      if (NESTED_CHILD_TYPES.includes(object_type)) {
        builder.addBuilderObjectFromApiToBuilder(object_type, object, true);
      }
    },
  });

  // same pseudo-update addObjectToBuilder would have pushed: undo marks the
  // object toBeDeleted, redo restores it
  groupedUpdates.push({
    object_type: object_type,
    object_id: created.id,
    setting_name: "[toBeDeleted]",
    value: undefined,
    old_value: true,
  });

  return created;
}

export default async function applyAssistantOperations(
  operations,
  { builder, contextData }
) {
  const idMap = {};
  const results = {
    created: [],
    updated: [],
    deleted: [],
    duplicated: [],
    errors: [],
  };
  const variant = contextData.objects.variant;
  let firstCreated = null;
  const allOperations = operations || [];

  // one history entry for the whole AI turn: all creates/updates/deletes are
  // collected here and committed via a single builder.update call below
  const groupedUpdates = [];

  // font-family values touched this turn, loaded after the commit so the
  // builder preview renders the real Google font
  const fontValues = new Set();

  // section_id -> [{ id, position }] for columns created earlier in this turn
  const createdColumnsBySection = {};
  const flexEnsuredSections = new Set();

  for (let index = 0; index < allOperations.length; index++) {
    const operation = allOperations[index];

    try {
      if (operation.op === "create") {
        const object_type = operation.object_type;
        let rawAttributes = operation.attributes;
        if (object_type === "field_options") {
          rawAttributes = normalizeFieldOptionAttributes(rawAttributes);
        }
        let payload = resolveAttributes(rawAttributes, idMap);
        collectFontValues(payload.options, fontValues);

        if (
          (object_type === "section_columns" || object_type === "elements") &&
          payload.section_id !== undefined &&
          payload.section_id !== null &&
          !flexEnsuredSections.has(String(payload.section_id))
        ) {
          await ensureSectionIsFlex(payload.section_id, variant, builder);
          flexEnsuredSections.add(String(payload.section_id));
        }

        // Reuse an existing column at this position instead of POSTing a
        // duplicate — duplicate position-1/2 columns were a common AI failure
        // mode and left content pointing at columns that looked fine until refresh.
        if (object_type === "section_columns") {
          const existing = findExistingSectionColumn(
            payload.section_id,
            payload.position,
            variant
          );
          if (existing) {
            if (operation.temp_id) {
              idMap[operation.temp_id] = existing.id;
            }
            const sectionKey = String(existing.section_id);
            createdColumnsBySection[sectionKey] =
              createdColumnsBySection[sectionKey] || [];
            if (
              !createdColumnsBySection[sectionKey].some(
                (column) => String(column.id) === String(existing.id)
              )
            ) {
              createdColumnsBySection[sectionKey].push({
                id: existing.id,
                position: existing.position,
              });
            }
            results.created.push({
              object_type: object_type,
              id: existing.id,
              reused: true,
            });
            continue;
          }
        }

        if (object_type === "elements") {
          payload = assignSectionColumnId(
            payload,
            variant,
            createdColumnsBySection
          );
        }

        // reviews live at the website level, outside the variant draft state
        if (object_type === "reviews") {
          const review = await new Promise((resolve, reject) => {
            apiRequest(
              "POST",
              `/websites/${variant.website_id}/reviews`,
              payload,
              (data) => {
                if (data && !data.error) {
                  resolve(data);
                } else {
                  reject(new Error((data && data.error) || "create failed"));
                }
              },
              { Accept: "application/json" }
            );
          });

          if (operation.temp_id) {
            idMap[operation.temp_id] = review.id;
          }
          results.created.push({ object_type: object_type, id: review.id });
          continue;
        }

        if (VARIANT_SCOPED_TYPES.includes(object_type)) {
          payload.variant_id = variant.id;
        }

        // When the batch also creates the nested section for this toggle, skip
        // the server auto-section so we don't end up with two sections.
        if (
          object_type === "content_toggle_items" &&
          laterSectionReferencesToggle(allOperations, index, operation.temp_id)
        ) {
          payload.create_initial_section = false;
        }

        if (object_type === "actions") {
          const { action_type, contactable_type, contactable_id, element_id } =
            payload;
          payload = {
            action_type,
            contactable_type,
            contactable_id,
          };
          if (element_id !== undefined && element_id !== null) {
            payload.element_id = element_id;
          }
          payload = { action_object: payload };
        }

        if (object_type === "conditions") {
          const {
            condition_type,
            contactable_type,
            contactable_id,
            keyword,
            data,
            operator,
          } = payload;
          payload = {
            condition_type,
            contactable_type,
            contactable_id,
          };
          const conditionOperator = String(operator || "").toLowerCase();
          if (conditionOperator === "and" || conditionOperator === "or") {
            payload.operator = conditionOperator;
          }
          if (CONDITION_ANSWER_MAPPING_TYPES.includes(condition_type)) {
            if (keyword !== undefined && keyword !== null) {
              payload.keyword = keyword;
            }
            if (data && typeof data === "object") {
              const filtered = {};
              CONDITION_ANSWER_DATA_KEYS.forEach((key) => {
                if (data[key] !== undefined && data[key] !== null) {
                  filtered[key] = data[key];
                }
              });
              if (Object.keys(filtered).length > 0) {
                payload.data = filtered;
              }
            }
          }
        }

        const agentOptions =
          object_type === "elements" &&
          payload.options &&
          typeof payload.options === "object"
            ? payload.options
            : {};

        const created = await createObject(
          builder,
          object_type,
          payload,
          groupedUpdates
        );

        if (object_type === "fields") {
          nestFieldOnElement(variant, created);
        }
        if (object_type === "field_options") {
          nestFieldOptionOnField(variant, created);
        }

        if (object_type === "section_columns") {
          const sectionKey = String(created.section_id);
          createdColumnsBySection[sectionKey] =
            createdColumnsBySection[sectionKey] || [];
          createdColumnsBySection[sectionKey].push({
            id: created.id,
            position: created.position,
          });
        }

        // "Jump to funnel step" confirmations need a target step other than the
        // current one. Default to the next funnel step when missing/invalid.
        if (object_type === "elements") {
          const confirmationType =
            payload.options?.["confirmation-type"] ||
            agentOptions["confirmation-type"];
          const redirectStep = payload.options?.["redirect-step"];
          const section = variant.sections[payload.section_id] || {};
          const currentStepId = created.step_id || section.step_id;
          const redirectIsValid =
            redirectStep === "previous" ||
            (redirectStep != null &&
              redirectStep !== "" &&
              String(redirectStep) !== String(currentStepId) &&
              Object.values(variant.steps || {}).some(
                (s) =>
                  String(s.id) === String(redirectStep) &&
                  s.toBeDeleted !== true
              ));

          if (confirmationType === "step" && !redirectIsValid) {
            const nextStepId = findNextStepId(
              variant,
              created,
              payload.section_id
            );
            if (nextStepId) {
              builder.update([
                {
                  object_type: "elements",
                  object_id: created.id,
                  setting_name: "[options][redirect-step]",
                  value: String(nextStepId),
                  skip_history: true,
                },
              ]);
            }
          }
        }

        if (operation.temp_id) {
          idMap[operation.temp_id] = created.id;
        }

        results.created.push({ object_type: object_type, id: created.id });

        if (!firstCreated) {
          firstCreated = { object_type: object_type, id: created.id };
        }

        // list creates seed "Item 1/2/3" placeholders server-side; drop them when
        // this turn also creates real content_list_items for the list
        if (
          object_type === "elements" &&
          payload.element_type === "list" &&
          batchCreatesListItemsFor(
            allOperations,
            index,
            operation.temp_id,
            created.id,
            idMap
          )
        ) {
          removeDefaultListPlaceholders(created, variant, groupedUpdates);
        }

        // Toggle creates return a nested section via to_prop — register it so
        // later element creates in this turn can target it (manual UI create
        // does the same in useContentToggle).
        if (
          object_type === "content_toggle_items" &&
          created.section &&
          created.section.id !== undefined &&
          created.section.id !== null
        ) {
          if (!variant.sections) variant.sections = {};
          variant.sections[created.section.id] = created.section;
          results.created.push({
            object_type: "sections",
            id: created.section.id,
          });
        }

        // plain row elements need a nested section created client-side
        // (conditional_row sections are created server-side)
        if (
          object_type === "elements" &&
          payload.element_type === "row" &&
          !laterSectionReferencesRow(allOperations, index, operation.temp_id)
        ) {
          const parentSection = variant.sections[payload.section_id] || {};
          const section = await createObject(
            builder,
            "sections",
            {
              variant_id: variant.id,
              step_id: created.step_id || parentSection.step_id || null,
              element_id: created.id,
              position: 1,
            },
            groupedUpdates
          );
          results.created.push({ object_type: "sections", id: section.id });
        }
      } else if (operation.op === "update") {
        const changes = buildUpdateChanges(operation, idMap);
        if (changes.length > 0) {
          changes.forEach((change) => {
            const key = change.setting_name.match(/\[options\]\[(.+)\]/)?.[1];
            if (
              isFontFamilyKey(key) &&
              typeof change.value === "string" &&
              change.value.trim()
            ) {
              fontValues.add(change.value.trim());
            }
          });
          groupedUpdates.push(...changes);
          results.updated.push({
            object_type: operation.object_type,
            id: changes[0].object_id,
          });

          if (operation.object_type === "fields") {
            nestFieldOnElement(
              variant,
              findRecord(variant.fields, changes[0].object_id)
            );
          }
          if (operation.object_type === "field_options") {
            nestFieldOptionOnField(
              variant,
              findRecord(variant.field_options, changes[0].object_id)
            );
          }
        }
      } else if (operation.op === "delete") {
        const object_id = resolveValue(operation.object_id, idMap);
        const object = (variant[operation.object_type] || {})[object_id];

        if (object && object.toBeDeleted !== true) {
          groupedUpdates.push({
            object_type: operation.object_type,
            object_id: object_id,
            setting_name: "[toBeDeleted]",
            value: true,
          });
          results.deleted.push({
            object_type: operation.object_type,
            id: object_id,
          });
        }
      } else if (operation.op === "duplicate") {
        if (!DUPLICATABLE_TYPES.includes(operation.object_type)) {
          results.errors.push(
            `duplicate is not supported for ${operation.object_type}`
          );
          continue;
        }

        const object_id = resolveValue(operation.object_id, idMap);
        dispatchCustomEvent("duplicateObject", {
          object_type: operation.object_type,
          object_id: object_id,
        });
        results.duplicated.push({
          object_type: operation.object_type,
          id: object_id,
        });
      }
    } catch (error) {
      results.errors.push(
        `${operation.op} ${operation.object_type} failed: ${
          error && error.message ? error.message : error
        }`
      );
    }
  }

  // commit the whole turn as one undo-able history entry
  if (groupedUpdates.length > 0) {
    builder.update(groupedUpdates);
  }

  loadFontFamilies(fontValues);

  if (firstCreated && builder.scrollToObjectWithRetry) {
    builder.scrollToObjectWithRetry(firstCreated.object_type, firstCreated.id);
  }

  return results;
}
